import { useQueryClient } from '@tanstack/react-query';
import { get_add_step_url } from '@/utils/urls.ts';
import { AddableStepType } from '@/types/automationFields.ts';
import { AUTOMATION_LATEST_VERSION_KEY } from '@/utils/queryKeys.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { useState } from 'react';
import { useEditVersionContext } from '@/context/EditVersionContext/useEditVersionContext.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { useEditorState } from '@/state/editorState.ts';
import { collectStepIds } from '@/utils/misc.ts';
import { useActiveStep } from '@/layouts/AutomationPageWrapper/hooks/useActiveStep.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';
import useCompositionOperations from '@/hooks/api/automations/useCompositionOperations.ts';
import { AutomationVersion } from '@/types/automations.ts';
import { notifyStepMutationError } from '@/services/stepMutationErrors.ts';
import { createLocalSubflowStepId } from '@/utils/localSubflowStepId.ts';

type addStepParams = {
  step_type: AddableStepType;
  step_id?: string;
  condition_id?: string;
  index: number;
};

const useAddStep = () => {
  const apiClient = useApiClient();
  const automationId = useAutomationId();
  const queryClient = useQueryClient();
  const { addToQueue, reportLatestDraftChanged } = useEditVersionContext();
  const [pendingCount, setPendingCount] = useState(0);

  const setNewStepId = useEditorState((s) => s.setNewStepId);

  const { setActiveStep } = useActiveStep();
  const { target, setActiveCompositionTarget } = useActiveCompositionTarget();
  const { mutate: applyComposition, isPending: isCompositionPending } = useCompositionOperations();

  const addStep = (params: addStepParams) => {
    if (target.scope_kind === 'local_subflow' && target.subflow_id) {
      const newStepId = createLocalSubflowStepId();
      applyComposition(
        [{
          op_type: 'create_local_subflow_step',
          subflow_id: target.subflow_id,
          step_id: newStepId,
          step_type: params.step_type,
          target_parent_step_id: params.step_id ?? null,
          target_child_group_key: params.condition_id ?? null,
          target_index: params.index,
        }],
        {
          onSuccess: () => {
            setNewStepId(newStepId);
            void setActiveCompositionTarget({
              scope_kind: 'local_subflow',
              subflow_id: target.subflow_id,
              step_id: newStepId,
            });
          },
        },
      );
      return;
    }
    setPendingCount((count) => count + 1);
    addToQueue({
      mutationFn: async (latestVersion) => {
        const oldStepIds = collectStepIds(latestVersion.steps);
        const response = await apiClient.put<AutomationVersion>(
          get_add_step_url(latestVersion.id),
          params,
          { suppressGlobalErrorToast: true },
        );
        const newStepIds = collectStepIds(response.data.steps);

        const newStepId = findNewStepId(oldStepIds, newStepIds);
        if (newStepId) {
          setNewStepId(newStepId);
          await setActiveStep(newStepId);
        }
        return response.data;
      },
      onSuccess: (version: AutomationVersion) => {
        const queryKey = AUTOMATION_LATEST_VERSION_KEY(automationId);
        queryClient.setQueryData(queryKey, version);
      },
      onError: (error) => {
        notifyStepMutationError('add step', error, reportLatestDraftChanged);
      },
      onComplete: () => {
        setPendingCount((count) => count - 1);
      },
    });
  };

  return { mutate: addStep, isPending: pendingCount > 0 || isCompositionPending };
};

function findNewStepId(oldIds: Set<string>, newIds: Set<string>) {
  for (const id of newIds) {
    if (!oldIds.has(id)) return id;
  }
  return null;
}

export default useAddStep;
